import { useQuery } from '@tanstack/react-query';
import { getCategories, getCategoriesGroup, getCategoriesSubgroup } from '@/api/category';
import { ReactQueryKey } from '@/constants/ReactQueryKeys';
import type { CategoriesGroup, Category } from '@/types/Category';
import type { QueryParamsInterface } from '@/types/Shared';

export const useCategories = ({
  onSuccess,
  onError,
  params,
  enabled = true,
}: {
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
  params?: QueryParamsInterface;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [ReactQueryKey.GET_CATEGORIES, params],
    queryFn: () => getCategories({ ...params }),
    onSuccess,
    onError,
    enabled: enabled && params !== undefined && !!params,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

export const useCategoryGroup = ({ onSuccess, onError }: { onSuccess?: (data: any) => void; onError?: (err: any) => void }) => {
  return useQuery({
    queryKey: ['get-categoriesGroup'],
    queryFn: getCategoriesGroup,
    onSuccess,
    onError,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

export const useCategorySubGroup = ({
  onSuccess,
  onError,
  group,
  waitUntilGroupResolved = false,
}: {
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
  group?: CategoriesGroup;
  /** When true, do not fetch until `group` is defined (avoids unscoped fetch while resolving Task group). */
  waitUntilGroupResolved?: boolean;
}) => {
  const fetchAll = group === undefined && !waitUntilGroupResolved;
  const fetchScoped = Boolean(group?.id);

  return useQuery({
    queryKey: ['get-categoriesSubGroup', fetchScoped ? group!.id : 'all'],
    queryFn: () => getCategoriesSubgroup({ groupId: fetchScoped ? group!.id : undefined }),
    onSuccess,
    onError,
    enabled: fetchAll || fetchScoped,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

interface PhaseCategoriesHookProps {
  phase: string;
}

const normalizePhaseName = (value: string) => value.trim().toLowerCase();

const findPhaseSubGroup = (subGroups: CategoriesGroup[] | undefined, normalizedPhase: string) =>
  subGroups?.find((cat: CategoriesGroup) => normalizePhaseName(cat.name) === normalizedPhase);

export const usePhaseCategories = ({ phase }: PhaseCategoriesHookProps) => {
  const normalizedPhase = normalizePhaseName(phase);

  const fetchCategoriesGroup = useCategoryGroup({});
  const groups = fetchCategoriesGroup.data?.data;
  const taskGroup = groups?.find((group: CategoriesGroup) => normalizePhaseName(group.name) === 'task');

  const fetchScopedSubGroups = useCategorySubGroup({ group: taskGroup, waitUntilGroupResolved: true });
  const fetchAllSubGroups = useQuery({
    queryKey: ['get-categoriesSubGroup', 'all'],
    queryFn: () => getCategoriesSubgroup({}),
    enabled: fetchCategoriesGroup.isSuccess,
    staleTime: 30000,
  });

  const categoriesSubGroup =
    findPhaseSubGroup(fetchScopedSubGroups.data?.data, normalizedPhase) ?? findPhaseSubGroup(fetchAllSubGroups.data?.data, normalizedPhase);

  const fetchPhaseCategories = useCategories({
    params: categoriesSubGroup?.id ? { subGroupId: categoriesSubGroup.id } : undefined,
    enabled: Boolean(categoriesSubGroup?.id),
  });

  const isLoading =
    fetchCategoriesGroup.isLoading || fetchScopedSubGroups.isLoading || fetchAllSubGroups.isLoading || fetchPhaseCategories.isLoading;

  const isFetching =
    fetchCategoriesGroup.isFetching || fetchScopedSubGroups.isFetching || fetchAllSubGroups.isFetching || fetchPhaseCategories.isFetching;

  return {
    ...fetchPhaseCategories,
    isLoading,
    isFetching,
  };
};

interface CategoryByIdHookProps {
  categoryId: number;
}

export const useCategoryById = ({ categoryId }: CategoryByIdHookProps) => {
  const fetchCategories = useCategories({});
  const category = fetchCategories?.data?.data?.find((cat: Category) => cat.id === categoryId);
  return category;
};
